import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';

import { asText, getAlertDuration, getAlertLabel, getAlertTypeLabel, getSeverityColor } from './streamAlerts';

const ALERT_TIME_FORMAT = 'YYYY/MM/DD HH:mm:ss';

const Section = styled.div`
   margin-bottom: 16px;
`;

// collapsed this is the whole alert display: one line right below the metric summary
const Strip = styled.button`
   width: 100%;
   display: flex;
   align-items: center;
   gap: 10px;
   background: #f9fafb;
   border: 1px solid #e5e7eb;
   border-left: 4px solid ${(props) => props.color};
   border-radius: 6px;
   padding: 9px 12px;
   font: inherit;
   text-align: left;
   cursor: pointer;

   &:hover {
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
   }

   /* no focus ring lingering after a click, keyboard focus stays visible */
   &:focus {
      outline: none;
   }

   &:focus-visible {
      outline: 2px solid #9ca3af;
      outline-offset: 2px;
   }
`;

const StripCount = styled.span`
   font-size: 10px;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   color: #6b7280;
   white-space: nowrap;
`;

const StripItems = styled.span`
   display: flex;
   align-items: center;
   gap: 14px;
   min-width: 0;
   overflow: hidden;
   white-space: nowrap;
`;

const StripItem = styled.span`
   display: flex;
   align-items: center;
   gap: 6px;
   min-width: 0;
   font-size: 12px;
   font-weight: 500;
   color: #374151;
   overflow: hidden;
   text-overflow: ellipsis;
`;

const Dot = styled.span`
   flex: none;
   width: 6px;
   height: 6px;
   border-radius: 50%;
   background-color: ${(props) => props.color};
`;

const StripDuration = styled.span`
   font-size: 12px;
   font-weight: 400;
   color: #6b7280;
`;

const Chevron = styled.span`
   margin-left: auto;
   flex: none;
   font-size: 10px;
   color: #6b7280;
   transform: rotate(${(props) => (props.$open ? '180deg' : '0deg')});
   transition: transform 0.2s ease;
`;

const AlertList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 12px;
   margin-top: 12px;
`;

const SnapshotTime = styled.div`
   margin-top: 12px;
   font-size: 10px;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   color: #6b7280;
   text-align: right;
`;

const AlertCard = styled.div`
   background: #f9fafb;
   border: 1px solid #e5e7eb;
   border-left: 4px solid ${(props) => props.color};
   border-radius: 6px;
   padding: 12px;
`;

const AlertHeader = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
   margin-bottom: 6px;
`;

const SeverityBadge = styled.span`
   background: ${(props) => props.color};
   color: #ffffff;
   border-radius: 10px;
   padding: 2px 8px;
   font-size: 10px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.5px;
`;

// same type scale as the metric cards in MetricsContainer
const AlertLabel = styled.span`
   color: #374151;
   font-size: 12px;
   font-weight: 500;
`;

const AlertMessage = styled.p`
   color: #6b7280;
   font-size: 12px;
   margin: 0 0 8px 0;
`;

const AlertFacts = styled.div`
   display: flex;
   gap: 20px;
   flex-wrap: wrap;
   font-size: 12px;
   color: #6b7280;

   .label {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
   }

   .value {
      font-weight: 600;
      color: #374151;
   }
`;

const StatusLine = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   background: #f9fafb;
   border: 1px solid #e5e7eb;
   border-radius: 6px;
   padding: 9px 12px;
   font-size: 12px;
   color: #6b7280;

   &::before {
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: ${(props) => props.color};
   }
`;

function AlertFact({ label, value }) {
   return (
      <div>
         <div className='label'>{label}</div>
         <div className='value'>{value}</div>
      </div>
   );
}

AlertFact.propTypes = {
   label: PropTypes.string,
   value: PropTypes.node,
};

function AlertEntry({ alert, snapshotTime }) {
   const color = getSeverityColor(alert.severity);
   const duration = getAlertDuration(alert.firstDetection, snapshotTime);

   return (
      <AlertCard color={color}>
         <AlertHeader>
            <SeverityBadge color={color}>{asText(alert.severity) || 'unknown'}</SeverityBadge>
            <AlertLabel>{getAlertLabel(alert)}</AlertLabel>
         </AlertHeader>
         {alert.message && <AlertMessage>{asText(alert.message)}</AlertMessage>}
         <AlertFacts>
            <AlertFact label='Alert Type' value={getAlertTypeLabel(alert) || 'unknown'} />
            <AlertFact
               label='Started [UTC]'
               value={alert.firstDetection ? moment.utc(alert.firstDetection).format(ALERT_TIME_FORMAT) : 'unknown'}
            />
            {duration && <AlertFact label='Duration' value={duration} />}
            <AlertFact label='Alert Code' value={asText(alert.code)} />
         </AlertFacts>
      </AlertCard>
   );
}

AlertEntry.propTypes = {
   alert: PropTypes.object.isRequired,
   snapshotTime: PropTypes.string,
};

function AlertsSection({ alerts, snapshotTime, isError }) {
   const [isOpen, setIsOpen] = useState(false);

   if (isError) {
      return (
         <Section>
            <StatusLine color='#f59e0b'>Alert data is currently unavailable.</StatusLine>
         </Section>
      );
   }

   if (alerts.length === 0) {
      return (
         <Section>
            <StatusLine color='#10b981'>No alerts detected for this stream.</StatusLine>
         </Section>
      );
   }

   // the alerts are sorted by severity, so the first one carries the color of the whole strip
   const stripColor = getSeverityColor(alerts[0].severity);

   return (
      <Section>
         <Strip type='button' color={stripColor} aria-expanded={isOpen} onClick={() => setIsOpen(!isOpen)}>
            <StripCount>{`${alerts.length} ${alerts.length === 1 ? 'Alert' : 'Alerts'}`}</StripCount>
            {!isOpen && (
               <StripItems>
                  {alerts.map((alert) => {
                     const duration = getAlertDuration(alert.firstDetection, snapshotTime);
                     return (
                        <StripItem key={alert.id}>
                           <Dot color={getSeverityColor(alert.severity)} />
                           {getAlertLabel(alert)}
                           {duration && <StripDuration>{duration}</StripDuration>}
                        </StripItem>
                     );
                  })}
               </StripItems>
            )}
            <Chevron $open={isOpen}>{'▾'}</Chevron>
         </Strip>

         {isOpen && (
            <>
               <AlertList>
                  {alerts.map((alert) => (
                     <AlertEntry key={alert.id} alert={alert} snapshotTime={snapshotTime} />
                  ))}
               </AlertList>
               {snapshotTime && (
                  <SnapshotTime>
                     {`Alert data as of ${moment.utc(snapshotTime).format(ALERT_TIME_FORMAT)} [UTC]`}
                  </SnapshotTime>
               )}
            </>
         )}
      </Section>
   );
}

AlertsSection.propTypes = {
   alerts: PropTypes.array,
   snapshotTime: PropTypes.string,
   isError: PropTypes.bool,
};

AlertsSection.defaultProps = {
   alerts: [],
};

export default AlertsSection;
